import { apiClient } from '@discovr/common';
import type { ReportExportDownload, ReportExportJob, ReportExportJobStatus } from '@discovr/types';
import { getData } from './shared';

const ACTIVE_STATUSES: ReadonlySet<ReportExportJobStatus> = new Set(['pending', 'processing', 'retrying']);
const READY_STATUS: ReportExportJobStatus = 'done';

function exportsUrl(campaignId: string): string {
  return `/campaigns/${encodeURIComponent(campaignId)}/report/exports`;
}

function jobUrl(campaignId: string, jobId: string): string {
  return `${exportsUrl(campaignId)}/${encodeURIComponent(jobId)}`;
}

export function isActiveReportExport(job: ReportExportJob): boolean {
  return ACTIVE_STATUSES.has(job.status);
}

/** A finished export whose file can be downloaded, e.g. the stored one a create reused. */
export function isReadyReportExport(job: ReportExportJob): boolean {
  return job.status === READY_STATUS;
}

/** Puts a job at the front of the list, or swaps it in place when it is already there. */
export function upsertReportExport(jobs: readonly ReportExportJob[] | undefined, job: ReportExportJob): ReportExportJob[] {
  const current = jobs ?? [];
  if (!current.some((existing) => existing.id === job.id)) return [job, ...current];
  return current.map((existing) => (existing.id === job.id ? job : existing));
}

export interface CreatedReportExport {
  job: ReportExportJob;
  /** True when the server answered 200: this call joined the campaign's active export, or got the
   *  finished one stored in the last day (status `done`). */
  joined: boolean;
}

const HTTP_OK = 200;

/** POST /campaigns/{id}/report/exports — 202 with a new job; 200 with a finished export from the last
 *  day, or with the campaign's existing active one. */
export async function createReportExport(campaignId: string): Promise<CreatedReportExport> {
  const res = await apiClient.post<{ data: ReportExportJob }>(exportsUrl(campaignId));
  return { job: res.data.data, joined: res.status === HTTP_OK };
}

/** GET /campaigns/{id}/report/exports — the latest 20, newest first. */
export function listReportExports(campaignId: string, signal?: AbortSignal): Promise<ReportExportJob[]> {
  return getData<ReportExportJob[]>(exportsUrl(campaignId), signal ? { signal } : undefined);
}

/** DELETE /campaigns/{id}/report/exports/{jobId} — a processing job stays processing with cancel_requested. */
export async function cancelReportExport(campaignId: string, jobId: string): Promise<ReportExportJob> {
  const res = await apiClient.delete<{ data: ReportExportJob }>(jobUrl(campaignId, jobId));
  return res.data.data;
}

/** GET .../download — a presigned URL (valid 60 seconds) as JSON. It is for the browser to open, never for apiClient. */
export function fetchReportExportDownload(campaignId: string, jobId: string): Promise<ReportExportDownload> {
  return getData<ReportExportDownload>(`${jobUrl(campaignId, jobId)}/download`);
}
